import { useEffect, useMemo,useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";

import config from "@/config";
import useIsFirstRender from "@/hooks/useIsFirstRender";
import HomeArtists from "@/pages/home/artists";
import HomeArtlists from "@/pages/home/artlists";
import HomeArtworks from "@/pages/home/artworks";
import HomeGalleries from "@/pages/home/galleries";
import HomeUsers from "@/pages/home/users";
import fetchWrapper from '@/services/fetchWrapper.service';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrDiscoverBootstrap } from '@/utils/ssrDiscoverBootstrap';

import Searchbar from './components/Searchbar';

import './index.css';

const SEARCH_QUERY_STORAGE_KEY = 'searchPageLastQuery';
const SEARCH_CATEGORIES = ['Artworks', 'Galleries', 'Artists', 'Users', 'Artlists'];
const isValidCategory = (category) => SEARCH_CATEGORIES.includes(category);
const MIN_TEXT_SEARCH_LENGTH = 2;

function normalizeSearchQueryForRequests(rawQuery = {}) {
  const nextQuery = rawQuery && typeof rawQuery === "object" ? { ...rawQuery } : {};
  const normalizedText = typeof nextQuery.q === "string" ? nextQuery.q.trim() : "";

  if (normalizedText.length > 0 && normalizedText.length < MIN_TEXT_SEARCH_LENGTH) {
    nextQuery.q = "";
  }

  return nextQuery;
}

function readStoredSearchQuery() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.sessionStorage.getItem(SEARCH_QUERY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (_) {
    return null;
  }
}

function writeStoredSearchQuery(value) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(SEARCH_QUERY_STORAGE_KEY, JSON.stringify(value));
  } catch (_) {
    // ignore write errors (private mode / disabled storage)
  }
}

function readInitialSearchSeoPayload() {
  const payload = getSsrDiscoverBootstrap('search');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function Search() {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultQuery = { sort: config.search.main_search.artwork_order_by[0] };
  const storedQuery = readStoredSearchQuery();

  const [query, setQuery] = useState(() => {
    if (location.state && typeof location.state === "object" && Object.prototype.hasOwnProperty.call(location.state, "sort")) {
      return location.state;
    }

    return storedQuery ? storedQuery : defaultQuery;
  });
  const isFirstRender = useIsFirstRender();
  const [searchSeo, setSearchSeo] = useState(() => readInitialSearchSeoPayload());
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const categoryFromState =
      location.state && typeof location.state === "object"
        ? location.state.category
        : undefined;

    if (isValidCategory(categoryFromState)) {
      return categoryFromState;
    }

    try {
      const stored = localStorage.getItem('searchSelectedCategory');
      if (isValidCategory(stored)) {
        return stored;
      }
      return 'Artworks';
    } catch (_) {
      return 'Artworks';
    }
  });

  const handleSelectedCategoryChange = (category) => {
    setSelectedCategory(category);
  };
  const requestQuery = useMemo(() => normalizeSearchQueryForRequests(query), [query]);

  useEffect(() => {
    /** Previously i used search params to store the query data (in order to restore it when we enter again to the search paage) 
         * but the url got way too long. That's why we will store it in the state of the url. The user will have a very short url (readable) */
    if(!isFirstRender) { 
      navigate("/search", { state: query, replace: true });
    }
  }, [query]);

  useEffect(() => {
    writeStoredSearchQuery(query);
  }, [query]);

  useEffect(() => {
    try {
      localStorage.setItem('searchSelectedCategory', selectedCategory);
    } catch (_) {
      // ignore write errors (private mode / disabled storage)
    }
  }, [selectedCategory]);

  useEffect(() => {
    const categoryFromState =
      location.state && typeof location.state === "object"
        ? location.state.category
        : undefined;

    if (isValidCategory(categoryFromState)) {
      setSelectedCategory(categoryFromState);
    }
  }, [location.state?.category]);

  useEffect(()=>{
    setTimeout(() => {
      document.body.classList.add("search-scrollbar");
    }, 50); /** Timeout prevents scrollbar from appearing on init */

    return () => {
      document.body.classList.remove("search-scrollbar");
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadSearchSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/discover/search`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setSearchSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load search SEO payload', error);
        }
      }
    }

    loadSearchSeo();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <StaticSeoHead
        title={searchSeo?.title || "Search Original Artworks | Suarte"}
        description={searchSeo?.metaDescription || "Search original artworks, artists, galleries, artlists, and art lovers on Suarte. Use advanced filters to discover, follow, and collect contemporary art from around the world."}
        canonicalUrl={searchSeo?.canonicalUrl}
        canonicalPath={searchSeo?.canonicalPath || "/search"}
        robots={searchSeo?.robots}
        ogType={searchSeo?.ogType}
        image={searchSeo?.ogImage}
        structuredData={searchSeo?.structuredData}
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Search', url: 'https://suarte.art/search' },
        ]}
      />
      <AppNavigationPage>
        <div className="search__page">
          <main className="search__main">
            <div className="search__sticky-main">
              <Searchbar
                query={query}
                setQuery={setQuery}
                categorySelected={handleSelectedCategoryChange}
                activeCategory={selectedCategory}
              />
            </div>
            {selectedCategory === 'Artworks' ? (
              <HomeArtworks fromSearch valueFromSearch={requestQuery} />
            ) : selectedCategory === 'Galleries' ? (
              <HomeGalleries fromSearch valueFromSearch={requestQuery} />
            ) : selectedCategory === 'Artists' ? (
              <HomeArtists fromSearch valueFromSearch={requestQuery} />
            ) : selectedCategory === 'Users' ? (
              <HomeUsers fromSearch valueFromSearch={requestQuery} />
            ) : selectedCategory === 'Artlists' ? (
              <HomeArtlists fromSearch valueFromSearch={requestQuery} />
            ) : null}
          </main>
        </div>
      </AppNavigationPage>
    </>
  );
}
